import React from 'react';
import { renderToStaticMarkup } from 'react-dom/server';
import PieChart, { Series, Label, Connector, Legend, Tooltip, Font, Border } from 'devextreme-react/pie-chart';
import Popover from 'devextreme-react/popover';
import { RiskMeterDto } from '../../../../dtos/RiskMeterDto';
import { RiskMeterSliceDto } from '../../../../dtos/RiskMeterSliceDto';
import { RiskMeterVariant, getVariantSettings } from '../types/riskMeterVariants';
import { getScoreColor, getBufferOverallColor, getBufferOverallColorWithAlpha, NOT_STARTED_COLOR } from '../utils/riskMeterColors';
import SliceTooltip from './SliceTooltip';
import FinancialSliceTooltip from './FinancialSliceTooltip';
import OverallRiskTooltip from './OverallRiskTooltip';
import './RiskMeter.css';

interface RiskMeterChartProps {
    data: RiskMeterDto;
    variant?: RiskMeterVariant;
    height?: number;
    width?: number;
    showLabels?: boolean;
    showConnectors?: boolean;
    showTooltip?: boolean;
    showCenterCircle?: boolean;
    showBackground?: boolean;
}

// The financial ring is a single full-circle point, so it needs one row with an arbitrary value
const FINANCIAL_RING = 'financial-ring';

// Gaps, band sizes, fonts and marks all come from the variant - see types/riskMeterVariants.ts.

// How a finished grant is drawn: a solid checkmark over a faded meter. COMPLETE_MUTE applies to the
// whole chart - rings, center and labels - so lowering it lightens everything; the checkmark is
// drawn outside that fade and stays at COMPLETE_MARK_OPACITY.
const COMPLETE_MARK_OPACITY = .6;
const COMPLETE_MUTE = 0.7;

export default function RiskMeterChart({
    data,
    variant = 'full',
    height,
    width,
    showLabels = true,
    showConnectors = true,
    showTooltip = true,
    showCenterCircle = true,
    showBackground = false
}: RiskMeterChartProps) {
    const settings = getVariantSettings(variant);

    // The icon is a fixed square in a grid cell. The full chart fixes only its height, leaving the
    // width to the container so the outside labels get as much room as there is.
    const dimensions = {
        height: height ?? settings.height,
        width: width ?? (settings.supportsText ? undefined : settings.width)
    };

    const financialScore = data.financial?.score ?? data.financialRisk ?? null;

    const customizeTooltip = (arg: any) => {
        const point = arg.point.data;

        if (point.tooltipKey === FINANCIAL_RING) {
            return { html: renderToStaticMarkup(<FinancialSliceTooltip financial={data.financial} score={financialScore} />) };
        }

        const slice = point as RiskMeterSliceDto & { value: number };
        return { html: renderToStaticMarkup(<SliceTooltip slice={slice} />) };
    };

    // Concentric rings share one data source, with a value field per ring. A row leaves the other
    // ring's field null so it contributes nothing there.
    const chartData: any[] = data.slices.map(slice => ({
        ...slice,
        slicesValue: slice.projected,
        financialValue: null,
        ring: 'slices'
    }));

    // One row drives the financial ring. Its value is arbitrary - a single point fills the ring. The
    // blank label keeps it out of the outer ring's labels.
    const financialData = [{
        label: '',
        tooltipKey: FINANCIAL_RING,
        slicesValue: null,
        financialValue: 1,
        score: financialScore,
        ring: 'financial'
    }];

    if (chartData.length === 0) {
        return (
            <div style={{
                textAlign: 'center',
                padding: settings.supportsText ? '50px' : '5px',
                color: '#999',
                height: dimensions.height,
                width: dimensions.width ?? '100%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
            }}>
                {settings.supportsText ? 'No risk data available' : '-'}
            </div>
        );
    }

    const effectiveWidth = dimensions.width ?? dimensions.height;
    const centerRadius = (Math.min(effectiveWidth, dimensions.height) / 2) * settings.bandFraction;

    const { sliceSeparator: sliceBorder, innerGap, outerGap } = settings;

    // Left unset when highlighting is on: the pie themes ship no hoverMode of their own, so naming
    // a value here would be inventing a default rather than restating one.
    const hoverMode = settings.highlightOnHover ? undefined : 'none';

    // Applied to the whole chart rather than each shape, so the fade covers labels and connectors as
    // well and overlapping edges do not darken where they meet.
    const completeOpacity = data.isComplete ? COMPLETE_MUTE : 1;

    const chart = (
        <PieChart
            dataSource={chartData.concat(financialData)}
            type="doughnut"
            innerRadius={settings.bandFraction}
            // Gap between the two rings. DevExtreme has no such option - it hardcodes 4px in
            // viz/components/consts - so patches/devextreme+22.2.11.patch adds it.
            {...{ seriesSpacing: outerGap }}
            startAngle={90}
            segmentsDirection="clockwise"
            size={dimensions}
            centerRender={showCenterCircle ? (component: any) => {
                // The pie shrinks to make room for outside labels, so take the hole's real radius
                // rather than deriving it from the container - otherwise the center would overlap
                // the financial ring.
                const innerRadius = component?.getInnerRadius?.();
                const hole = innerRadius && innerRadius > 0 ? innerRadius : centerRadius;
                // The disc stops innerGap short of the hole. The financial ring's border reaches
                // half its width further in, so the gap that renders is wider - see the border
                // math in riskMeterVariants.ts.
                const radius = hole - innerGap;

                // DevExtreme centers this template on the pie by its own bounding box, so the circle
                // is drawn at the origin. Wrapping it in an <svg> would hand it that element's
                // default size as the bounding box and throw the centering off.
                //
                // An odd ring border makes DevExtreme shift the rings half a pixel to keep the line
                // crisp (SHARPING_CORRECTION), and the template does not get that shift. Setting cx
                // cannot correct it - the bounding box moves with the circle and the centering
                // cancels it out. Extending the box 1px to the top-left instead puts its center at
                // -0.5, so the circle lands half a pixel further down-right, matching the rings.
                // The marker is always rendered, never conditionally: DevExtreme clears this group on
                // every redraw, so a changing child count leaves React trying to remove nodes that
                // are already gone. Only its offset changes - at -r it sits inside the circle's box
                // and does nothing, at -(r + 1) it extends the box and produces the half pixel.
                const r = Math.max(radius, 1);
                const bias = sliceBorder % 2 !== 0 ? 1 : 0;

                return (
                    <g>
                        <circle
                            id="risk-meter-center-circle"
                            cx={0}
                            cy={0}
                            r={r}
                            fill={getBufferOverallColor(data.overallScore)}
                            style={{ cursor: 'pointer' }}
                        />
                        <rect x={-(r + bias)} y={-(r + bias)} width={1} height={1} fill="none" stroke="none" />
                    </g>
                );
            } : undefined}
            customizePoint={(point: any) => {
                const isFinancial = point.data.ring === 'financial';
                const score = point.data.score;
                const color = score !== null && score !== undefined
                    ? (isFinancial ? getBufferOverallColor(score) : getScoreColor(score))
                    : NOT_STARTED_COLOR;

                return {
                    color,
                    border: { visible: true, width: sliceBorder, color: '#ffffff' }
                };
            }}
        >
            {/* Series order runs inside out, so the financial ring is declared first to sit between
                the center and the slices. */}
            {/* Middle band: financial risk, a single point filling the ring */}
            <Series argumentField="label" valueField="financialValue" hoverMode={hoverMode}>
                <Label visible={false} />
            </Series>
            {/* Outer band: the phase and milestone slices */}
            <Series argumentField="label" valueField="slicesValue" hoverMode={hoverMode}>
                <Label visible={showLabels && settings.supportsText} position="outside" customizeText={(point: any) => `${point.argumentText}`}>
                    <Font size={settings.labelFontSize} color="#000000" weight={700} />
                    <Connector visible={showConnectors && showLabels && settings.supportsText} width={settings.connectorWidth} color="#000000" />
                    <Border visible={true} width={settings.labelBorderWidth} color="#ffffff" />
                </Label>
            </Series>
            <Legend visible={false} />
            {showTooltip && settings.supportsText && <Tooltip enabled={true} customizeTooltip={customizeTooltip} />}
        </PieChart>
    );

    const horizontalPadding = settings.horizontalPadding;

    // Use DevExtreme's native hover events to avoid React state changes that cause re-renders
    const popover = settings.supportsText && (
        <Popover
            target="#risk-meter-center-circle"
            showEvent="mouseenter"
            hideEvent="mouseleave"
        >
            <OverallRiskTooltip data={data} />
        </Popover>
    );

    // Laid over the whole meter, so it reads as "this grant is done" without hiding the bands
    const completeMark = data.isComplete && (
        <svg
            className="risk-meter-complete-mark"
            viewBox="0 0 100 100"
            preserveAspectRatio="xMidYMid meet"
            aria-label="Grant complete"
        >
            <path
                d="M22 52 L42 72 L78 28"
                fill="none"
                stroke="#2E7D32"
                strokeOpacity={COMPLETE_MARK_OPACITY}
                strokeWidth={settings.completeMarkStrokeWidth}
                strokeLinecap="round"
                strokeLinejoin="round"
            />
        </svg>
    );

    const body = (
        <div className="risk-meter-chart-wrap">
            <div style={{ opacity: completeOpacity }}>{chart}</div>
            {completeMark}
        </div>
    );

    if (showBackground) {
        return (
            <div style={{ backgroundColor: getBufferOverallColorWithAlpha(data.overallScore, 0.25), paddingLeft: horizontalPadding, paddingRight: horizontalPadding }}>
                {body}
                {popover}
            </div>
        );
    }

    return (
        <div style={{ paddingLeft: horizontalPadding, paddingRight: horizontalPadding }}>
            {body}
            {popover}
        </div>
    );
}
